import React, { useState, useRef, useEffect } from 'react';
import { Save, Calendar } from 'lucide-react';
import './AccountManagement.css';
import { useUser } from '../context/UserContext';

const AccountManagement: React.FC = () => {
  const { userData, saveUserData } = useUser();

  const [formData, setFormData] = useState({
    firstName: userData?.personalInfo?.firstName || '',
    lastName: userData?.personalInfo?.lastName || '',
    dateOfBirth: userData?.personalInfo?.dateOfBirth || '',
    address: userData?.personalInfo?.address || '',
    phone: userData?.personalInfo?.phone || '',
    email: userData?.personalInfo?.email || '',
  });

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date(2024, 0));
  const datePickerRef = useRef<HTMLDivElement>(null);

  const [deviceInfo] = useState({
    name: 'Galaxy Watch 6',
    battery: 76,
    status: 'Connected via Bluetooth',
    lastSync: '12 minutes ago',
  });



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateSelect = (day: number) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateString = newDate.toISOString().split('T')[0];
    setFormData(prev => ({
      ...prev,
      dateOfBirth: dateString
    }));
    setShowDatePicker(false);
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  if (!userData) return null;

  const handleSave = () => {
    if (!userData) return;
    const updatedUserData = {
      ...userData,
      personalInfo: {
        ...userData.personalInfo,
        ...formData,
        fullName: `${formData.firstName} ${formData.lastName}`.trim(),
      }
    };
    saveUserData(updatedUserData);
    console.log('Saving changes:', formData);
    alert('Changes saved successfully!');
  };

  return (
    <div className="account-management">
      <h1 className="account-title">Account Management</h1>

      <div className="account-grid">
        {/* Personal Information - Editable */}
        <div className="account-card">
          <h2 className="card-heading">Personal Information</h2>

          <div className="form-group">
            <label htmlFor="firstName" className="form-label">First Name</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName" className="form-label">Last Name</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="dateOfBirth" className="form-label">Date of Birth</label>
            <div className="date-picker-container" ref={datePickerRef}>
              <div className="date-input-wrapper">
                <input
                  type="text"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  readOnly
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="form-input date-input"
                  placeholder="YYYY-MM-DD"
                />
                <button
                  type="button"
                  className="calendar-button"
                  onClick={() => setShowDatePicker(!showDatePicker)}
                >
                  <Calendar size={18} />
                </button>
              </div>
              {showDatePicker && (
                <div className="calendar-popup">
                  <div className="calendar-header">
                    <button type="button" onClick={handlePrevMonth} className="nav-button">←</button>
                    <span className="month-year">
                      {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </span>
                    <button type="button" onClick={handleNextMonth} className="nav-button">→</button>
                  </div>
                  <div className="calendar-weekdays">
                    <div className="weekday">Sun</div>
                    <div className="weekday">Mon</div>
                    <div className="weekday">Tue</div>
                    <div className="weekday">Wed</div>
                    <div className="weekday">Thu</div>
                    <div className="weekday">Fri</div>
                    <div className="weekday">Sat</div>
                  </div>
                  <div className="calendar-days">
                    {Array.from({ length: getFirstDayOfMonth(currentMonth) }).map((_, i) => (
                      <div key={`empty-${i}`} className="calendar-day empty"></div>
                    ))}
                    {Array.from({ length: getDaysInMonth(currentMonth) }).map((_, i) => {
                      const day = i + 1;
                      const dateStr = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toISOString().split('T')[0];
                      const isSelected = dateStr === formData.dateOfBirth;
                      return (
                        <button
                          key={day}
                          type="button"
                          className={`calendar-day ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleDateSelect(day)}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>



          <div className="form-group">
            <label htmlFor="address" className="form-label">Address</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone" className="form-label">Phone</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="form-input"
            />
          </div>

          <button className="save-button" onClick={handleSave}>
            <Save size={16} />
            Save Changes
          </button>
        </div>

        {/* Existing Conditions */}
        <div className="account-card">
          <h2 className="card-heading">Existing Conditions</h2>
          <ul className="conditions-list">
            <li>Hypertension</li>
            <li>High Cholesterol</li>
            <li>Type-2 Diabetes</li>
          </ul>
        </div>



        {/* My Devices */}
        <div className="account-card">
          <h2 className="card-heading">My Devices</h2>

          <div className="device-info">
            <div className="device-item">
              <span className="device-label">Device:</span>
              <span className="device-value">{deviceInfo.name}</span>
            </div>

            <div className="device-item">
              <span className="device-label">Battery:</span>
              <div className="battery-container">
                <div className="battery-bar">
                  <div
                    className="battery-fill"
                    style={{ width: `${deviceInfo.battery}%` }}
                  ></div>
                </div>
                <span className="battery-text">{deviceInfo.battery}%</span>
              </div>
            </div>

            <div className="device-item">
              <span className="device-label">Status:</span>
              <span className="device-value status-connected">{deviceInfo.status}</span>
            </div>

            <div className="device-item">
              <span className="device-label">Last Sync:</span>
              <span className="device-value">{deviceInfo.lastSync}</span>
            </div>

            <button className="pair-button">Pair New Device</button>

            <button className="search-button">
              Search for Android Wearables
            </button>
          </div>
        </div>

        {/* Assigned Clinician */}
        <div className="account-card">
          <h2 className="card-heading">Assigned Clinician</h2>
          <div className="info-list">
            <div className="info-item">
              <span className="info-label">Name:</span>
              <span className="info-value">Dr. Michael Smith</span>
            </div>
            <div className="info-item">
              <span className="info-label">Practice:</span>
              <span className="info-value">Capitol Health Group</span>
            </div>
            <div className="info-item">
              <span className="info-label">Address:</span>
              <span className="info-value">1234 Wellness Ave, Suite 200, Springfield, VA</span>
            </div>
            <div className="info-item">
              <span className="info-label">Email:</span>
              <span className="info-value">drsmith@capitolhealth.com</span>
            </div>
            <div className="info-item">
              <span className="info-label">Phone:</span>
              <span className="info-value">(555) 987-6543</span>
            </div>
            <div className="info-item">
              <span className="info-label">Emergency:</span>
              <span className="info-value">(555) 000-1111</span>
            </div>
          </div>
        </div>



        {/* Emergency Contacts */}
        <div className="account-card emergency-contacts">
          <h2 className="card-heading">Emergency Contacts</h2>
          <div className="emergency-contacts-list">
            <div className="emergency-contact-item">
              <div className="contact-header">
                <h3 className="contact-name">John {userData.personalInfo.lastName}</h3>
                <span className="contact-relationship">Spouse</span>
              </div>
              <div className="info-list">
                <div className="info-item">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">(555) 123-4568</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email:</span>
                  <span className="info-value">john.{userData.personalInfo.lastName.toLowerCase()}@example.com</span>
                </div>
              </div>
            </div>

            <div className="emergency-contact-item">
              <div className="contact-header">
                <h3 className="contact-name">Emily {userData.personalInfo.lastName}</h3>
                <span className="contact-relationship">Daughter</span>
              </div>
              <div className="info-list">
                <div className="info-item">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">(555) 234-5678</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email:</span>
                  <span className="info-value">emily.{userData.personalInfo.lastName.toLowerCase()}@example.com</span>
                </div>
              </div>
            </div>

            <div className="emergency-contact-item">
              <div className="contact-header">
                <h3 className="contact-name">Dr. Michael Smith</h3>
                <span className="contact-relationship">Primary Care Physician</span>
              </div>
              <div className="info-list">
                <div className="info-item">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">(555) 987-6543</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Emergency:</span>
                  <span className="info-value emergency-number">(555) 000-1111</span>
                </div>
              </div>
            </div>

            <div className="emergency-contact-item">
              <div className="contact-header">
                <h3 className="contact-name">911</h3>
                <span className="contact-relationship">Emergency Services</span>
              </div>
              <div className="info-list">
                <div className="info-item">
                  <span className="info-label">Call:</span>
                  <span className="info-value emergency-number">911</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountManagement;
